import type { RealtimeJobStatus } from '@quipex/core/realtime';
import type { ActivityItem, ActivityJob, ActivityJobUpdate } from './models';

const ACTIVE_ACTIVITY_JOB_STATUSES = new Set<RealtimeJobStatus>([
    'Queued',
    'Started',
    'Progress',
]);
const FAILED_ACTIVITY_JOB_STATUSES = new Set<RealtimeJobStatus>([
    'Failed',
    'Expired',
]);
const DOCUMENT_PROCESSING_JOB_TYPES = new Set([
    'DocumentImport',
    'SharePointImport',
    'DocumentClassification',
    'DocumentIntelligence',
]);

export function mergeActivityJob(
    existing: ActivityJob,
    update: ActivityJobUpdate
): ActivityJob {
    const withSelection =
        existing.jobType === 'SelectedBuildingDocumentExport' &&
        !existing.selectedSelectionKey &&
        update.selectedSelectionKey
            ? { ...existing, selectedSelectionKey: update.selectedSelectionKey }
            : existing;
    const existingTime = Date.parse(existing.occurredAtUtc);
    const updateTime = Date.parse(update.occurredAtUtc);

    if (updateTime < existingTime) {
        return withSelection;
    }

    if (
        isTerminalActivityJobStatus(existing.status) &&
        !isTerminalActivityJobStatus(update.status)
    ) {
        return withSelection;
    }

    const next: ActivityJob = {
        ...toActivityJob(update),
        buildingPublicId: update.buildingPublicId ?? existing.buildingPublicId,
        documentPublicId: update.documentPublicId ?? existing.documentPublicId,
        packId: update.packId ?? existing.packId,
        selectedSelectionKey:
            update.selectedSelectionKey ?? existing.selectedSelectionKey,
        downloadAvailable:
            update.downloadAvailable ?? existing.downloadAvailable,
        expiresAt: update.expiresAt ?? existing.expiresAt,
        exportKind: update.exportKind ?? existing.exportKind ?? null,
        buildingName: update.buildingName ?? existing.buildingName ?? null,
        message:
            existing.jobType === 'SelectedBuildingDocumentExport'
                ? update.message
                : (update.message ?? existing.message),
        warning:
            existing.jobType === 'SelectedBuildingDocumentExport'
                ? update.warning
                : update.warning || existing.warning,
        completedItems: update.completedItems ?? existing.completedItems,
        totalItems: update.totalItems ?? existing.totalItems,
    };

    return activityJobsEqual(existing, next) ? existing : next;
}

export function toActivityJob(update: ActivityJobUpdate): ActivityJob {
    return {
        jobId: update.jobId,
        jobType: update.jobType,
        stage: update.stage,
        status: update.status,
        warning: update.warning,
        percentComplete: update.percentComplete,
        completedItems: update.completedItems,
        totalItems: update.totalItems,
        message: update.message,
        occurredAtUtc: update.occurredAtUtc,
        documentPublicId: update.documentPublicId,
        buildingPublicId: update.buildingPublicId,
        packId: update.packId,
        selectedSelectionKey: update.selectedSelectionKey,
        downloadAvailable: update.downloadAvailable,
        expiresAt: update.expiresAt,
        exportKind: update.exportKind ?? null,
        buildingName: update.buildingName ?? null,
    };
}

export function compareActivityJobsNewestFirst(
    left: ActivityJob,
    right: ActivityJob
): number {
    return Date.parse(right.occurredAtUtc) - Date.parse(left.occurredAtUtc);
}

export function representativeActivityJob(
    jobs: readonly ActivityJob[]
): ActivityJob {
    const newestFirst = [...jobs].sort(compareActivityJobsNewestFirst);
    const representative =
        newestFirst.find((job) => isFailedActivityJobStatus(job.status)) ??
        newestFirst.find((job) => job.warning) ??
        newestFirst.find((job) => isActiveActivityJobStatus(job.status)) ??
        newestFirst[0];

    if (!representative) {
        throw new Error('Activity items require at least one job.');
    }

    return representative;
}

export function presentationActivityJob(item: ActivityItem): ActivityJob {
    return representativeActivityJob(item.jobs);
}

export function newestActivityTimestamp(left: string, right: string): string {
    return Date.parse(right) > Date.parse(left) ? right : left;
}

export function stableActivityBuildingPublicId(
    jobs: readonly ActivityJob[]
): string | null {
    const buildingPublicIds = new Set(
        jobs.flatMap((job) =>
            job.buildingPublicId ? [job.buildingPublicId] : []
        )
    );

    return buildingPublicIds.size === 1
        ? (buildingPublicIds.values().next().value ?? null)
        : null;
}

export function hasConflictingActivityBuildingIds(
    jobs: readonly ActivityJob[]
): boolean {
    return (
        new Set(
            jobs.flatMap((job) =>
                job.buildingPublicId ? [job.buildingPublicId] : []
            )
        ).size > 1
    );
}

export function hasConflictingActivityLineageBuildingIds(
    items: readonly ActivityItem[],
    correlationId: string
): boolean {
    return (
        new Set(
            items
                .filter((item) => item.correlationId === correlationId)
                .flatMap((item) =>
                    item.jobs.flatMap((job) =>
                        job.buildingPublicId ? [job.buildingPublicId] : []
                    )
                )
        ).size > 1
    );
}

export function quarantineConflictingActivityLineages(
    items: readonly ActivityItem[]
): readonly ActivityItem[] {
    const correlationIds = new Set(
        items.flatMap((item) =>
            item.correlationId === null ? [] : [item.correlationId]
        )
    );
    const conflictedCorrelationIds = new Set(
        [...correlationIds].filter((correlationId) =>
            hasConflictingActivityLineageBuildingIds(items, correlationId)
        )
    );
    const quarantinedProcessingCorrelationIds = new Set(
        [...correlationIds].filter((correlationId) =>
            hasQuarantinedProcessingActivity(items, correlationId)
        )
    );
    if (
        conflictedCorrelationIds.size === 0 &&
        quarantinedProcessingCorrelationIds.size === 0
    ) {
        return items;
    }

    return items.map((item) =>
        item.correlationId !== null &&
        (conflictedCorrelationIds.has(item.correlationId) ||
            (quarantinedProcessingCorrelationIds.has(item.correlationId) &&
                isDocumentProcessingActivity(item))) &&
        (item.availability !== 'unavailable' ||
            item.seen ||
            !item.identityQuarantined)
            ? {
                  ...item,
                  availability: 'unavailable',
                  identityQuarantined: true,
                  seen: false,
              }
            : item
    );
}

function hasQuarantinedProcessingActivity(
    items: readonly ActivityItem[],
    correlationId: string
): boolean {
    return items.some(
        (item) =>
            item.correlationId === correlationId &&
            item.identityQuarantined === true &&
            isDocumentProcessingActivity(item)
    );
}

function isDocumentProcessingActivity(item: ActivityItem): boolean {
    return item.jobs.some((job) =>
        DOCUMENT_PROCESSING_JOB_TYPES.has(job.jobType)
    );
}

export function isActiveActivityJobStatus(status: RealtimeJobStatus): boolean {
    return ACTIVE_ACTIVITY_JOB_STATUSES.has(status);
}

export function isCompletedActivityJobStatus(
    status: RealtimeJobStatus
): boolean {
    return status === 'Completed';
}

export function isFailedActivityJobStatus(status: RealtimeJobStatus): boolean {
    return FAILED_ACTIVITY_JOB_STATUSES.has(status);
}

export function isSkippedActivityJobStatus(status: RealtimeJobStatus): boolean {
    return status === 'Skipped';
}

export function isTerminalActivityJobStatus(
    status: RealtimeJobStatus
): boolean {
    return !isActiveActivityJobStatus(status);
}

function activityJobsEqual(left: ActivityJob, right: ActivityJob): boolean {
    return (
        left.jobId === right.jobId &&
        left.jobType === right.jobType &&
        left.stage === right.stage &&
        left.status === right.status &&
        left.warning === right.warning &&
        left.percentComplete === right.percentComplete &&
        left.completedItems === right.completedItems &&
        left.totalItems === right.totalItems &&
        left.message === right.message &&
        left.occurredAtUtc === right.occurredAtUtc &&
        left.documentPublicId === right.documentPublicId &&
        left.buildingPublicId === right.buildingPublicId &&
        left.packId === right.packId &&
        left.exportKind === right.exportKind &&
        left.downloadAvailable === right.downloadAvailable &&
        left.selectedSelectionKey === right.selectedSelectionKey &&
        left.expiresAt === right.expiresAt &&
        left.buildingName === right.buildingName
    );
}
